import { Box, Button, Flex, Link, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { ResistomapSelect } from '@resistapp/client/components/forms/resistomap-select';
import { MultiOptionConfirmModal } from '@resistapp/client/components/modals/multi-option-confirmation-modal';
import { ProjectSelector } from '@resistapp/client/components/project-selector/project-selector';
import { fetchOrThrow, useDeleteOrganisationProject, useOrganisation } from '@resistapp/client/hooks/api';
import { useToastWithoutDebounce } from '@resistapp/client/hooks/use-resistomap-toast';
import { isFailedToFetchError } from '@resistapp/client/utils/error';
import { downloadDataUrl, getImageDataUrlFromHtml, getImageDataUrlFromSVG } from '@resistapp/client/utils/exporting';
import type { AdminUserDirectoryUser } from '@resistapp/common/api-types';
import { resistomapOrganisationId } from '@resistapp/common/features';
import { NormalisedValueName } from '@resistapp/common/normalisation-mode';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import type {
  FullProject,
  NormalisationMode,
  OrganisationSummary,
  PartialDict,
  PossibleZoomableLevel,
  Project,
} from '@resistapp/common/types';
import { ProjectSampleAction, ProjectType } from '@resistapp/common/types';
import { GroupBase } from 'chakra-react-select';
import { chain, map } from 'lodash';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// Reanalysis of a project with an old lab sheet requires project deletion,
// and moving or copying samples will complicate deep deletion and thus re-analysis of old projects.
// (we'd probably need to manually add sample ids to the new lab sheet)
const FIRST_PROJECT_ID_WITH_NEW_LAB_SHEET = 2300;

export type LevelOption = {
  value: `${string}-${number}`;
  label: string;
  country: string;
};

export type LevelsWithZoomableAreasOption = {
  label: string; // Country name
  options: LevelOption[];
};

export const actionOptions = [
  { value: ProjectSampleAction.ADD, label: ProjectSampleAction.ADD },
  { value: ProjectSampleAction.MOVE, label: ProjectSampleAction.MOVE },
  { value: ProjectSampleAction.REMOVE, label: ProjectSampleAction.REMOVE },
] as const;

export const exportOptions = [
  { value: 1, label: 'Please choose target', id: '', type: '', plotType: '' },
  {
    value: 2,
    label: 'Detected genes chart',
    id: 'research-view-detected-genes-chart',
    type: 'chart',
    plotType: 'bar',
  },
  {
    value: 3,
    label: 'Samples chart',
    id: 'research-view-samples-chart',
    type: 'chart',
    plotType: 'box',
  },
  {
    value: 4,
    label: 'Gene details chart',
    id: 'research-view-gene-details-chart',
    type: 'chart',
    plotType: 'heat',
  },
  {
    value: 5,
    label: 'Detected genes legend',
    id: TestSelectors.DETECTED_GENES_LEGEND,
    type: 'legend',
    plotType: 'bar',
  },
  {
    value: 6,
    label: 'Samples legend',
    id: TestSelectors.SAMPLES_LEGEND,
    type: 'legend',
    plotType: 'box',
  },
  {
    value: 7,
    label: 'Gene details legend',
    id: TestSelectors.HEATMAP_LEGEND,
    type: 'legend',
    plotType: 'heat',
  },
] as const;

export type ProjectSampleAccessInfo = {
  sampleId: number;
  samplingPointId: number | null;
};

export function getZoomableLevelsOptions(
  levelsWithZoomableAreas: PartialDict<PossibleZoomableLevel[]> | undefined,
): LevelsWithZoomableAreasOption[] {
  return Object.entries(levelsWithZoomableAreas || {}).map(([country, levels]) => ({
    label: country,
    options: levels
      ? levels
          .sort((a, b) => a.valueOf() - b.valueOf())
          .map(level => ({
            value: `${country}-${level}`,
            label: `${country}: ${level}`,
            country,
          }))
      : [],
  }));
}

export function getZoomableLevelsValues(
  allLevelsWithZoomableAreas: PartialDict<PossibleZoomableLevel[]> | undefined,
): LevelOption[] {
  return Object.entries(allLevelsWithZoomableAreas || {}).reduce<LevelOption[]>((acc, [country, options]) => {
    if (!options) {
      return acc;
    }
    acc.push(
      ...options.map(level => ({
        value: `${country}-${level}` as const,
        label: `${country}: ${level}`,
        country,
      })),
    );
    return acc;
  }, []);
}

export function createOverlay(textContent: string) {
  const overlayElement = document.createElement('div');
  overlayElement.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  `;

  const content = document.createElement('div');
  content.style.cssText = `    background-color: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  `;

  content.textContent = textContent;

  overlayElement.appendChild(content);
  document.body.appendChild(overlayElement);

  return overlayElement;
}

export async function onExport(
  exportObject: (typeof exportOptions)[number],
  project: FullProject,
  normalisationMode: NormalisationMode,
) {
  // The user didn't choose any type to export
  if (!exportObject.id) {
    return;
  }

  const overlay = createOverlay('Exporting the image...');
  const fileName = `${project.id}_${NormalisedValueName[normalisationMode].replace(/\s+/g, '-')}_${exportObject.label}`;

  if (exportObject.type === 'chart') {
    const padding =
      exportObject.plotType === 'heat' ? { left: 130, bottom: 60, right: 1 } : { left: 0, bottom: -10, right: 5 };
    const element = document.getElementById(exportObject.id);
    if (!(element instanceof SVGElement)) {
      throw new Error(`Element with id ${exportObject.id} is not an SVGElement`);
    }
    // When you want to preview the element on the page to check it, instead of downloading it, use keepElement: true
    try {
      const dataUrl = await getImageDataUrlFromSVG(element, exportObject.plotType, { padding, keepElement: false });
      if (dataUrl) {
        downloadDataUrl(dataUrl, `${fileName}.png`);
      }
    } catch (error) {
      if (isFailedToFetchError(error)) {
        console.warn('Failed to export image due to network issue');
      } else {
        console.error('Error exporting SVG:', error);
      }
    } finally {
      document.body.removeChild(overlay);
    }
  } else {
    const element = document.getElementById(exportObject.id) as HTMLElement;
    try {
      const dataUrl = await getImageDataUrlFromHtml(element, true);
      if (dataUrl) {
        downloadDataUrl(dataUrl, `${fileName}.png`);
      }
    } catch (error) {
      if (isFailedToFetchError(error)) {
        console.warn('Failed to export image due to network issue');
      } else {
        console.error('Error exporting PNG:', error);
      }
    } finally {
      document.body.removeChild(overlay);
    }
  }
}

export async function changeAdminLevels(
  levels: LevelOption[],
  patchLevels: (newLevels: Record<string, number[]>) => Promise<any>,
) {
  try {
    const newLevels = chain(levels)
      .groupBy('country')
      .mapValues(options => map(options, opt => parseInt(opt.value.split('-')[1])))
      .value();
    await patchLevels(newLevels);
  } catch (error) {
    console.error('Failed to update admin levels:', error);
    alert('Updating the administrative levels failed!');
  }
}

interface SampleManagementFormProps {
  project: Project;
  action: ProjectSampleAction;
  setAction: (action: ProjectSampleAction) => void;
  actionProjectId: number | undefined;
  setActionProjectId: (id: number | undefined) => void;
  pristineProjectSamples: boolean;
  setPristineProjectSamples: (pristine: boolean) => void;
  projectSamplesError: string | null;
  onManageSamples: () => void;
  isLoading: boolean;
}

export function SampleManagementForm({
  project,
  action,
  setAction,
  actionProjectId,
  setActionProjectId,
  pristineProjectSamples,
  setPristineProjectSamples,
  projectSamplesError,
  onManageSamples,
  isLoading,
}: SampleManagementFormProps) {
  return (
    <Flex flexDirection="column">
      {project.id < FIRST_PROJECT_ID_WITH_NEW_LAB_SHEET ||
        (actionProjectId && actionProjectId < FIRST_PROJECT_ID_WITH_NEW_LAB_SHEET && (
          <Box mt={2} minW="150px" maxW="350px">
            <Text fontWeight="bold" color="red">
              Moving/copying samples to/from an old project, will complicate deletion & re-analysis the source project.
            </Text>
          </Box>
        ))}
      {project.type === ProjectType.POOLED && (
        <Box mt={2} minW="150px" maxW="350px">
          <Text fontWeight="bold" color="red">
            Pooled projects not supported.
          </Text>
        </Box>
      )}
      <Flex alignItems="center" gap={2}>
        <ResistomapSelect<{ value: string; label: string }, false, GroupBase<{ value: string; label: string }>>
          options={actionOptions}
          chakraStyles={{ control: base => ({ ...base, backgroundColor: 'white' }) }}
          value={{
            value: actionOptions.find(o => o.value === action)?.label as string,
            label: actionOptions.find(o => o.value === action)?.label as string,
          }}
          onChange={option => {
            if (option && 'value' in option) {
              const actionValue = option.value as ProjectSampleAction;
              setAction(actionValue);
              if (actionValue === ProjectSampleAction.REMOVE) {
                setActionProjectId(undefined);
              }
            }
            setPristineProjectSamples(false);
          }}
          onBlur={() => {
            setPristineProjectSamples(false);
          }}
          placeholder="Select action..."
          closeMenuOnSelect={true}
          styles={{
            container: provided => ({
              ...provided,
              width: '100px',
            }),
            control: provided => ({
              ...provided,
              minHeight: '32px',
              height: '32px',
            }),
            valueContainer: provided => ({
              ...provided,
              height: '32px',
              padding: '0 6px',
            }),
            input: provided => ({
              ...provided,
              margin: '0px',
            }),
          }}
        />
        <Box fontWeight="bold">samples {action === ProjectSampleAction.REMOVE ? 'from this' : 'to'} project</Box>
      </Flex>
      <Flex>
        {action !== ProjectSampleAction.REMOVE && (
          <ProjectSelector
            disableNavigation
            onProjectSelect={projectId => {
              setActionProjectId(projectId);
              setPristineProjectSamples(false);
            }}
            value={actionProjectId}
            minWidth="200px"
            maxWidth="600px"
          />
        )}
      </Flex>
      {projectSamplesError && !pristineProjectSamples && <div style={{ color: 'red' }}>{projectSamplesError}</div>}
      <Button
        onClick={onManageSamples}
        isLoading={isLoading}
        isDisabled={Boolean(projectSamplesError || pristineProjectSamples || project.type === ProjectType.POOLED)}
      >
        <span style={{ textTransform: 'capitalize' }}>
          {actionOptions.find(o => o.value === action)?.label.toLowerCase()}{' '}
        </span>
        &nbsp;samples
      </Button>
    </Flex>
  );
}

export function OrganisationAccessListItem({
  organisation,
  project,
  organisationUsers,
  projectSampleAccessInfo,
}: {
  organisation: OrganisationSummary;
  project: Project;
  organisationUsers: AdminUserDirectoryUser[];
  projectSampleAccessInfo: readonly ProjectSampleAccessInfo[];
}) {
  const deleteAccess = useDeleteOrganisationProject(organisation.id, project.id);
  const requiresAccessDetails = organisation.id !== resistomapOrganisationId;
  const {
    data: organisationWithAccesses,
    isLoading: isLoadingAccesses,
    error: accessDetailsError,
  } = useOrganisation(requiresAccessDetails ? organisation.id : Number.NaN);
  const showToast = useToastWithoutDebounce();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGrantingMissing, setIsGrantingMissing] = useState(false);

  const sampleCoverage = useMemo(() => {
    if (!requiresAccessDetails) {
      return {
        accessible: projectSampleAccessInfo.length,
        total: projectSampleAccessInfo.length,
      };
    }

    const sampleAccessSet = new Set(organisationWithAccesses?.sampleAccesses ?? []);
    const samplingPointAccessSet = new Set(organisationWithAccesses?.samplingPointAccesses ?? []);
    let accessible = 0;

    projectSampleAccessInfo.forEach(({ sampleId, samplingPointId }) => {
      if (sampleAccessSet.has(sampleId)) {
        accessible += 1;
        return;
      }

      if (samplingPointId != null && samplingPointAccessSet.has(samplingPointId)) {
        accessible += 1;
        return;
      }
    });

    return {
      accessible,
      total: projectSampleAccessInfo.length,
    };
  }, [organisationWithAccesses, projectSampleAccessInfo, requiresAccessDetails]);

  const hasMissingAccess =
    !isLoadingAccesses && sampleCoverage.total > 0 && sampleCoverage.accessible < sampleCoverage.total;

  if (accessDetailsError) {
    throw accessDetailsError;
  }

  const perform = async (strategy: 'keep' | 'revoke') => {
    const response = await (strategy === 'keep' ? deleteAccess.keep() : deleteAccess.revoke());
    const isSuccess = response.status !== undefined && response.status >= 200 && response.status <= 204;
    if (!isSuccess) {
      const serverMessage = (response.data as unknown as { message?: string } | undefined)?.message;
      const message = serverMessage || response.statusText || 'Failed to update access';
      showToast({ title: 'Failed to update access', description: message, status: 'error' });
      throw new Error(message);
    }
    window.location.reload();
  };

  const handleGrantMissing = async () => {
    if (!hasMissingAccess) {
      return;
    }
    setIsGrantingMissing(true);
    try {
      const response = await fetchOrThrow<undefined, boolean>(
        'POST',
        `/organisation/${organisation.id}/project/${project.id}?scope=samples`,
      );
      const status = response.status ?? 0;
      if (status < 200 || status > 204) {
        const message = response.statusText || 'Failed to grant access';
        throw new Error(message);
      }
      window.location.reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to grant access';
      showToast({ title: 'Failed to grant access', description: message, status: 'error' });
    } finally {
      setIsGrantingMissing(false);
    }
  };

  return (
    <ListItem>
      <Box>
        <Flex align="baseline" gap={2} wrap="wrap">
          <Link as={RouterLink} to={`/admin/organisation/${organisation.id}`} fontWeight="semibold" color="blue.600">
            {organisation.name}
          </Link>
          {isLoadingAccesses ? (
            <Text as="span" fontSize="sm" color="gray.500">
              (loading sample access…)
            </Text>
          ) : hasMissingAccess ? (
            <Text as="span" fontSize="sm" fontWeight="semibold" color="red.500">
              ({sampleCoverage.accessible}/{sampleCoverage.total})
            </Text>
          ) : null}
          {hasMissingAccess && organisation.id !== resistomapOrganisationId ? (
            <Button
              size="xs"
              variant="outline"
              colorScheme="red"
              isLoading={isGrantingMissing}
              onClick={() => {
                void handleGrantMissing();
              }}
            >
              Grant missing
            </Button>
          ) : null}
          {organisation.id !== resistomapOrganisationId ? (
            <Button
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => {
                setIsModalOpen(true);
              }}
            >
              Remove
            </Button>
          ) : null}
        </Flex>
        {organisation.id !== resistomapOrganisationId ? (
          <MultiOptionConfirmModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
            }}
            title={`Remove ${organisation.name}'s access`}
            message={`Choose how ${organisation.name} should lose access to ${project.name}.`}
            options={[
              {
                key: 'keep',
                label: 'Remove project access only',
                variant: 'outline',
                colorScheme: 'blue',
                description: 'Keep sampling point and sample permissions unchanged.',
                onSelect: async () => {
                  await perform('keep');
                },
              },
              {
                key: 'revoke',
                label: 'Remove project and sampling access',
                colorScheme: 'red',
                description: 'Revoke sampling point and sample permissions granted via this project.',
                onSelect: async () => {
                  await perform('revoke');
                },
              },
            ]}
          />
        ) : null}
        {organisationUsers.length > 0 ? (
          <UnorderedList listStyleType="circle" ml={6} mt={1} spacing={1}>
            {organisationUsers.map(user => (
              <ListItem key={user.id}>
                {user.firstName} {user.lastName}
              </ListItem>
            ))}
          </UnorderedList>
        ) : (
          <Text fontSize="sm" color="gray.600" ml={6} mt={1}>
            No users from this organisation currently inherit project access.
          </Text>
        )}
      </Box>
    </ListItem>
  );
}

export function Warnings({ warnings }: { warnings: string[] | undefined }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      {warnings?.length ? warnings.map((warning, key) => <div key={key}> {warning}</div>) : 'None'}
    </div>
  );
}
